import { CostsProps } from '@app/components/Costs/Costs';
import { isExpressShipping } from '@app/data/clients/cartCheckoutClient';
import { AppliedCoupon } from '@app/types/cart/Coupon';
import {
  DEFAULT_SHIPPING_METHOD,
  toGqlShippingMethod,
} from '@app/types/cart/ShippingMethod';
import {
  Cart,
  Discount,
  EstimatedShipping,
  Message,
  Summary,
  Tax,
} from '@app/types/monolith/cart';

type Maybe<T> = T | null | undefined;

// NOTE(jj): We are narrowing the very wide Cart type, to only the values we are using
// to simplify testing
export type NarrowedCart = {
  messages: Array<Pick<Message, 'type'>>;
  summary: {
    tax: Maybe<Pick<Tax, 'amount'>>;
    estimated_shipping: Pick<
      EstimatedShipping,
      | 'allow_express'
      | 'express'
      | 'standard'
      | 'preference'
      | 'express_ex_tax'
      | 'standard_ex_tax'
    >;
  } & Pick<
    Summary,
    | 'total'
    | 'show_tax_separately'
    | 'undiscounted_subtotal'
    | 'subtotal'
    | 'subtotal_ex_tax'
    | 'undiscounted_subtotal_ex_tax'
    | 'is_tax_computed'
  >;
  applied_coupon: Maybe<
    Pick<AppliedCoupon, 'code' | 'type'> & {
      sub_discount: Pick<Discount, 'amount'>;
    }
  >;
} & Pick<Cart, 'currency' | 'total_items_count'>;

type TypeCompatibilityCheck<T, _B extends T> = T;

// NOTE(jj): this type check will fail if type Cart (from monolith) is not assignable to NarrowedCart
type _IsNarrowCartCompatible = TypeCompatibilityCheck<NarrowedCart, Cart>;

const shippingCostsFromCart = (
  estimatedShipping: NarrowedCart['summary']['estimated_shipping']
) => {
  const shippingMethod = estimatedShipping.preference
    ? toGqlShippingMethod(estimatedShipping.preference)
    : DEFAULT_SHIPPING_METHOD;

  const isExpress = isExpressShipping(estimatedShipping);

  const shippingAmountExcludingTax = isExpress
    ? estimatedShipping.express_ex_tax
    : estimatedShipping.standard_ex_tax;
  const shippingAmountWithTax = isExpress
    ? estimatedShipping.express
    : estimatedShipping.standard;

  const shippingEstimateTaxAmount =
    shippingAmountWithTax - shippingAmountExcludingTax;

  return {
    shippingMethod,
    shippingAmountExcludingTax,
    shippingAmountWithTax,
    shippingEstimateTaxAmount,
  };
};

const parseCoupon = ({
  code,
  type,
  sub_discount,
}: NonNullable<NarrowedCart['applied_coupon']>) => {
  return {
    code,
    type,
    discountAmount: Number(sub_discount.amount),
  };
};

const getFlatRateShippingApplied = (messages?: NarrowedCart['messages']) => {
  return messages?.some(({ type }) => type === 'flat_rate_shipping_applied');
};

export const transformMonolithCartToCostProps = ({
  currency,
  summary: {
    tax,
    total,
    show_tax_separately: showTaxSeparately,
    undiscounted_subtotal,
    undiscounted_subtotal_ex_tax,
    subtotal_ex_tax,
    subtotal: summarySubtotal,
    estimated_shipping,
    is_tax_computed,
  },
  total_items_count: lineItemsCount,
  messages,
  applied_coupon,
}: NarrowedCart): Omit<CostsProps, 'pendingTaxText'> => {
  const flatRateApplied = getFlatRateShippingApplied(messages);

  const subtotal = showTaxSeparately ? subtotal_ex_tax : summarySubtotal;

  const undiscountedSubtotal = showTaxSeparately
    ? undiscounted_subtotal_ex_tax
    : undiscounted_subtotal;

  const {
    shippingMethod,
    shippingAmountExcludingTax,
    shippingAmountWithTax,
    shippingEstimateTaxAmount,
  } = shippingCostsFromCart(estimated_shipping);

  const appliedCoupon = applied_coupon
    ? parseCoupon(applied_coupon)
    : undefined;

  return {
    currency,
    showTaxSeparately,
    lineItemsCount,
    total,
    undiscountedSubtotal,
    subtotal,
    taxAmount: tax?.amount,
    shippingAmountExcludingTax,
    shippingAmountWithTax,
    shippingEstimateTaxAmount,
    flatRateApplied,
    appliedCoupon,
    shippingMethod,
    isTaxComputed: is_tax_computed,
  };
};
